/**
 * Centralized API utility for handling geolocation and lead submission
 */

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data with fallback
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    
    // Validate we have required data
    if (data && data.data && data.data.ipAddress) {
      return data;
    } else {
      throw new Error('Invalid geolocation data structure');
    }
  } catch (error) {
    console.error('Geolocation API error:', error);
    
    // Return fallback data structure with default IP
    return {
      data: {
        ipAddress: '0.0.0.0',
        cityName: 'Unknown',
        regionName: 'Unknown',
        countryCode: 'US'
      }
    };
  }
};

/**
 * Submit lead data to CRM
 * @param {Object} formData - Form data from user
 * @param {string} formData.name - User's full name
 * @param {string} formData.email - User's email
 * @param {number} formData.countryDialCode - Country dial code (e.g., 1 for US)
 * @param {string} formData.country - Country code (e.g., "US")
 * @param {string} formData.phoneNumber - Phone number (numeric only)
 * @param {string} formData.description - User's message/description
 * @param {string} [formData.packageTitle] - Optional package title
 * @param {number} [formData.packageAmount] - Optional package amount
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData) => {
  try {
    // Get geolocation data (with fallback)
    const geoData = await getGeoLocation();
    
    // Extract numeric-only phone number
    const numericPhone = formData.phoneNumber.replace(/\D/g, '');
    
    // Prepare payload - ensure required fields are not empty
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryDialCode,
      country: formData.country,
      phoneNumber: numericPhone,
      ipAddress: geoData?.data?.ipAddress || '0.0.0.0',
      description: formData.description || '',
      pageUrl: window.location.href,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    // Clone response before reading to avoid "body stream already read" error
    const responseClone = response.clone();
    
    let result;
    try {
      result = await response.json();
    } catch (jsonError) {
      // If JSON parsing fails, try with the clone
      try {
        result = await responseClone.json();
      } catch (cloneError) {
        // If both fail, return text or empty object
        result = { message: 'Failed to parse response' };
      }
    }

    // Check if response body contains internal statusCode (some APIs return 200 with error inside)
    const actualStatusCode = result.statusCode || response.status;
    const isSuccess = actualStatusCode === 200 && !result.statusName?.includes('Error');

    return {
      success: isSuccess,
      statusCode: actualStatusCode,
      data: result
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      statusCode: 500,
      data: {
        errorMessage: error.message || 'An unexpected error occurred. Please try again.'
      }
    };
  }
};

/**
 * Handle API response and perform appropriate action
 * @param {Object} response - API response from submitLead
 * @param {Function} onSuccess - Callback for successful submission
 * @param {Function} onError - Callback for error
 */
export const handleLeadResponse = (response, onSuccess, onError) => {
  const { statusCode, data } = response;

  if (statusCode === 200 && data.data?.customerId) {
    // Success - redirect to application page
    const customerId = data.data.customerId;
    window.location.href = `/application/main?customerid=${customerId}`;
    if (onSuccess) onSuccess(data);
  } else if (statusCode === 200) {
    // HTTP 200 but no customerId - something went wrong
    const errorMsg = data.message || data.errorMessage || data.error || 'Submission completed but no confirmation received. Please contact us directly.';
    if (onError) onError(errorMsg);
  } else if (statusCode === 401) {
    // Unauthorized - show specific message from response
    const errorMsg = data.errorMessage || data.message || data.error || 'Unauthorized access';
    if (onError) onError(errorMsg);
  } else if (statusCode === 400) {
    // Bad request - show specific errorMessage from response
    const errorMsg = data.errorMessage || data.message || data.error || 'Invalid data provided. Please check your information and try again.';
    if (onError) onError(errorMsg);
  } else {
    // Other errors - try multiple fields for error message
    const errorMsg = data.errorMessage || data.message || data.error || 'Something went wrong. Please try again.';
    if (onError) onError(errorMsg);
  }
};

/**
 * List of countries with dial codes
 */
export const countries = [
  { code: 'US', name: 'United States', dialCode: 1 },
  { code: 'CA', name: 'Canada', dialCode: 1 },
  { code: 'GB', name: 'United Kingdom', dialCode: 44 },
  { code: 'AU', name: 'Australia', dialCode: 61 },
  { code: 'NZ', name: 'New Zealand', dialCode: 64 },
  { code: 'IN', name: 'India', dialCode: 91 },
  { code: 'PK', name: 'Pakistan', dialCode: 92 },
  { code: 'BD', name: 'Bangladesh', dialCode: 880 },
  { code: 'AE', name: 'United Arab Emirates', dialCode: 971 },
  { code: 'SA', name: 'Saudi Arabia', dialCode: 966 },
  { code: 'DE', name: 'Germany', dialCode: 49 },
  { code: 'FR', name: 'France', dialCode: 33 },
  { code: 'IT', name: 'Italy', dialCode: 39 },
  { code: 'ES', name: 'Spain', dialCode: 34 },
  { code: 'NL', name: 'Netherlands', dialCode: 31 },
  { code: 'BE', name: 'Belgium', dialCode: 32 },
  { code: 'CH', name: 'Switzerland', dialCode: 41 },
  { code: 'AT', name: 'Austria', dialCode: 43 },
  { code: 'SE', name: 'Sweden', dialCode: 46 },
  { code: 'NO', name: 'Norway', dialCode: 47 },
  { code: 'DK', name: 'Denmark', dialCode: 45 },
  { code: 'FI', name: 'Finland', dialCode: 358 },
  { code: 'IE', name: 'Ireland', dialCode: 353 },
  { code: 'PL', name: 'Poland', dialCode: 48 },
  { code: 'CZ', name: 'Czech Republic', dialCode: 420 },
  { code: 'HU', name: 'Hungary', dialCode: 36 },
  { code: 'RO', name: 'Romania', dialCode: 40 },
  { code: 'BG', name: 'Bulgaria', dialCode: 359 },
  { code: 'GR', name: 'Greece', dialCode: 30 },
  { code: 'PT', name: 'Portugal', dialCode: 351 },
  { code: 'JP', name: 'Japan', dialCode: 81 },
  { code: 'CN', name: 'China', dialCode: 86 },
  { code: 'KR', name: 'South Korea', dialCode: 82 },
  { code: 'SG', name: 'Singapore', dialCode: 65 },
  { code: 'MY', name: 'Malaysia', dialCode: 60 },
  { code: 'TH', name: 'Thailand', dialCode: 66 },
  { code: 'PH', name: 'Philippines', dialCode: 63 },
  { code: 'ID', name: 'Indonesia', dialCode: 62 },
  { code: 'VN', name: 'Vietnam', dialCode: 84 },
  { code: 'BR', name: 'Brazil', dialCode: 55 },
  { code: 'MX', name: 'Mexico', dialCode: 52 },
  { code: 'AR', name: 'Argentina', dialCode: 54 },
  { code: 'CL', name: 'Chile', dialCode: 56 },
  { code: 'CO', name: 'Colombia', dialCode: 57 },
  { code: 'ZA', name: 'South Africa', dialCode: 27 },
  { code: 'NG', name: 'Nigeria', dialCode: 234 },
  { code: 'EG', name: 'Egypt', dialCode: 20 },
  { code: 'KE', name: 'Kenya', dialCode: 254 },
  { code: 'IL', name: 'Israel', dialCode: 972 },
  { code: 'TR', name: 'Turkey', dialCode: 90 },
  { code: 'RU', name: 'Russia', dialCode: 7 },
  { code: 'UA', name: 'Ukraine', dialCode: 380 },
];
